import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { getApiBase } from "../apiBase";
import { buildPluginsHeader } from "./registry";
import { useBoardTheme } from "./boardThemeStore";
import { isCommercialProjectRow } from "./commercialProjectAcf";
import { summaryGroup } from "../projectGridColumns";
import { fileUrlFromStoredValue } from "./CommercialFieldFileDisplay";
import { isProjectFieldFileContentUrl } from "../projectFieldFileAuth";
import { workOrderBaseForCommercialRow } from "./commercialWorkOrderGroup";
import {
  COMMERCIAL_SERVICE_ORDER_FIELD_KEYS,
} from "./commercialServiceOrderFields";

ModuleRegistry.registerModules([AllCommunityModule]);

const AUTH_TOKEN_KEY = "projectBoardAuthToken";
const LEGACY_BOARD_HOST = "10.88.1.193";

function authHeaders() {
  const h = { ...buildPluginsHeader() };
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) h.Authorization = `Bearer ${token}`;
  } catch {
    /* ignore */
  }
  return h;
}

function candidatesFromSummary(summary) {
  if (!summary || typeof summary !== "object") return [];
  const out = [];
  for (const key of COMMERCIAL_SERVICE_ORDER_FIELD_KEYS) {
    const url = fileUrlFromStoredValue(summary[key]);
    if (url) out.push({ url, fieldKey: key });
  }
  // Signed WO is often the PDF users mean when SO fields are empty.
  const signedWo = fileUrlFromStoredValue(summary.signed_work_order);
  if (signedWo) out.push({ url: signedWo, fieldKey: "signed_work_order" });
  return out;
}

function pickPreferredCandidate(candidates) {
  if (!candidates?.length) return null;
  const local = candidates.find((c) => isProjectFieldFileContentUrl(c.url));
  if (local) return local;
  return candidates[0];
}

function serviceOrderCandidateForRow(row, parentByWoBase) {
  const own = pickPreferredCandidate(candidatesFromSummary(summaryGroup(row)));
  if (own) return { ...own, inherited: false };

  const wo = String(row?.work_order || "").trim();
  if (!wo.includes(".") || !parentByWoBase) return null;
  const base = workOrderBaseForCommercialRow(row);
  const parent = base && base !== wo ? parentByWoBase.get(base) : null;
  if (!parent) return null;
  const fromParent = pickPreferredCandidate(candidatesFromSummary(summaryGroup(parent)));
  if (!fromParent) return null;
  return { ...fromParent, inherited: true };
}

function serviceOrderSourceLabel(candidate) {
  if (!candidate?.url) return "None";
  const t = String(candidate.url).trim();
  const inherited = candidate.inherited ? " (parent)" : "";
  if (isProjectFieldFileContentUrl(t)) return `Local${inherited}`;
  try {
    const host = new URL(t).hostname;
    if (host === LEGACY_BOARD_HOST) return `Legacy board${inherited}`;
    if (host) return `External${inherited}`;
  } catch {
    /* ignore */
  }
  return `Stored${inherited}`;
}

function phaseForRow(row) {
  const s = summaryGroup(row);
  return String(s?.phase || row?.phase || "").trim();
}

function enrichCommercialRows(allProjects) {
  const commercial = allProjects.filter(isCommercialProjectRow);
  const parentByWoBase = new Map();
  for (const row of commercial) {
    const wo = String(row?.work_order || "").trim();
    if (!wo || wo.includes(".")) continue;
    parentByWoBase.set(wo, row);
  }
  return commercial.map((row) => {
    const candidate = serviceOrderCandidateForRow(row, parentByWoBase);
    return {
      ...row,
      _soUrl: candidate?.url || "",
      _soSource: serviceOrderSourceLabel(candidate),
      _soInherited: Boolean(candidate?.inherited),
      _phase: phaseForRow(row),
    };
  });
}

export default function ServiceOrderExportPanel() {
  const { agGridTheme } = useBoardTheme();
  const gridRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const closeModal = useCallback(() => {
    if (exporting) return;
    setOpen(false);
  }, [exporting]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeModal]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setStatus("");
    setSelectedCount(0);
    const base = getApiBase();
    axios
      .get(`${base}/projects`, { headers: authHeaders() })
      .then((res) => {
        if (cancelled) return;
        const all = Array.isArray(res.data) ? res.data : [];
        setRows(enrichCommercialRows(all));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || "Could not load projects.");
        setRows([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || loading) return undefined;
    const id = requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    return () => cancelAnimationFrame(id);
  }, [open, loading, rows.length]);

  const columnDefs = useMemo(
    () => [
      {
        colId: "__select__",
        headerName: "",
        width: 48,
        pinned: "left",
        lockPosition: true,
        resizable: false,
        sortable: false,
        filter: false,
        suppressMovable: true,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
      },
      {
        field: "work_order",
        headerName: "Work order",
        width: 120,
        filter: "agTextColumnFilter",
      },
      {
        field: "name",
        headerName: "Project",
        flex: 1.4,
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        field: "customer",
        headerName: "Customer",
        flex: 1,
        minWidth: 120,
        filter: "agTextColumnFilter",
      },
      {
        field: "_phase",
        headerName: "Phase",
        width: 120,
        filter: "agTextColumnFilter",
      },
      {
        field: "project_type",
        headerName: "Type",
        width: 110,
        filter: "agTextColumnFilter",
        valueGetter: (p) =>
          p.data?.project_type ||
          summaryGroup(p.data)?.project_type ||
          "",
      },
      {
        field: "_soSource",
        headerName: "Service order",
        width: 150,
        filter: "agSetColumnFilter",
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
    }),
    []
  );

  const onSelectionChanged = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) {
      setSelectedCount(0);
      return;
    }
    setSelectedCount(api.getSelectedRows()?.length || 0);
  }, []);

  async function runExport() {
    const api = gridRef.current?.api;
    const selected = api?.getSelectedRows?.() || [];
    if (!selected.length) {
      setError("Select at least one project.");
      return;
    }
    const ids = selected.map((r) => r.id).filter((id) => Number.isFinite(Number(id)));
    if (!ids.length) {
      setError("Select at least one project.");
      return;
    }

    setExporting(true);
    setError("");
    setStatus(`Preparing export for ${ids.length} project${ids.length === 1 ? "" : "s"}… Large legacy batches can take several minutes.`);
    try {
      const base = getApiBase();
      const res = await axios.post(
        `${base}/admin/commercial/service-orders.zip`,
        { ids },
        {
          headers: authHeaders(),
          responseType: "blob",
          timeout: 30 * 60 * 1000,
        }
      );
      const stamp = new Date().toISOString().slice(0, 10);
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `service-orders-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);

      const fileCount = res.headers?.["x-service-order-file-count"];
      const skipped = res.headers?.["x-service-order-skipped-count"];
      const failed = res.headers?.["x-service-order-failed-count"];
      const parts = [];
      if (fileCount != null) parts.push(`${fileCount} file${Number(fileCount) === 1 ? "" : "s"}`);
      if (skipped != null && Number(skipped) > 0) parts.push(`${skipped} skipped`);
      if (failed != null && Number(failed) > 0) parts.push(`${failed} failed`);
      setStatus(
        parts.length
          ? `Download started (${parts.join(", ")}).`
          : "Download started."
      );
    } catch (err) {
      let msg = "Export failed.";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) msg = parsed.error;
        } catch {
          /* ignore */
        }
      } else if (typeof data?.error === "string") {
        msg = data.error;
      } else if (err?.message) {
        msg = err.message;
      }
      setError(msg);
      setStatus("");
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <section className="plugins-card plugins-card-wide" style={{ padding: "1rem 1.25rem", marginBottom: "1rem" }}>
        <h3 className="plugins-card-name" style={{ marginTop: 0 }}>
          Service order export
        </h3>
        <p className="plugins-card-desc">
          Download service order PDFs for commercial projects as a ZIP. Files stored on this board and
          on the legacy Project Board ({LEGACY_BOARD_HOST}) are included.
        </p>
        <button
          type="button"
          className="email-triggers-button email-triggers-button-primary"
          onClick={() => setOpen(true)}
        >
          Export service orders
        </button>
        {!open && status ? (
          <p className="email-triggers-muted" role="status" style={{ marginTop: 8 }}>
            {status}
          </p>
        ) : null}
        {!open && error ? (
          <p className="email-triggers-muted" role="alert" style={{ marginTop: 8, color: "#b45309" }}>
            {error}
          </p>
        ) : null}
      </section>

      {open ? (
        <div
          className="email-triggers-modal-backdrop"
          role="presentation"
          onClick={closeModal}
        >
          <div
            className="email-triggers-modal service-order-export-modal"
            style={{
              maxWidth: "min(1100px, 96vw)",
              width: "100%",
              maxHeight: "min(92vh, 820px)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="service-order-export-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="service-order-export-title" className="email-triggers-modal-title">
              Export service orders
            </h3>
            <p className="email-triggers-modal-sub" style={{ marginBottom: 10 }}>
              Filter and select commercial projects, then export. Legacy files are fetched from{" "}
              {LEGACY_BOARD_HOST} when needed — large selections (especially legacy) can take several
              minutes; duplicate URLs are only downloaded once.
            </p>

            <div
              className="service-order-export-grid-wrap"
              style={{
                flex: "1 1 auto",
                height: "min(52vh, 480px)",
                minHeight: 320,
                width: "100%",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              {loading ? (
                <p className="email-triggers-muted" style={{ padding: 12 }}>
                  Loading commercial projects…
                </p>
              ) : (
                <div style={{ height: "100%", width: "100%" }}>
                  <AgGridReact
                    ref={gridRef}
                    theme={agGridTheme}
                    rowData={rows}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowSelection="multiple"
                    suppressRowClickSelection
                    animateRows={false}
                    onSelectionChanged={onSelectionChanged}
                    getRowId={(p) => String(p.data?.id ?? "")}
                  />
                </div>
              )}
            </div>

            {error ? (
              <p className="email-triggers-muted" role="alert" style={{ marginTop: 8, color: "#b45309" }}>
                {error}
              </p>
            ) : null}
            {status ? (
              <p className="email-triggers-muted" role="status" style={{ marginTop: 8 }}>
                {status}
              </p>
            ) : null}

            <div className="email-triggers-modal-actions" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="email-triggers-button email-triggers-button-primary"
                disabled={exporting || loading || selectedCount === 0}
                onClick={runExport}
              >
                {exporting ? "Exporting…" : `Export (${selectedCount})`}
              </button>
              <button
                type="button"
                className="email-triggers-button email-triggers-button-ghost"
                disabled={exporting}
                onClick={closeModal}
              >
                Cancel
              </button>
              <span className="email-triggers-muted" style={{ alignSelf: "center", fontSize: 12 }}>
                {rows.length.toLocaleString()} commercial project{rows.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
